import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import AddIcon from "@mui/icons-material/Add"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined"
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import * as hrAttendanceService from "../../services/hrAttendanceService"


// =========================================================
// CONSTANTES
// =========================================================

const TODAY =
  new Date()
    .toISOString()
    .slice(0, 10)


const DEFAULT_WORK_START_TIME =
  "08:00"


const TIME_24H_REGEX =
  /^([01]\d|2[0-3]):([0-5]\d)$/


const EMPTY_FORM = {
  employee_id: "",
  attendance_date: TODAY,

  check_in: "",
  check_out: "",

  status: "PRESENT",

  source: "MANUAL",
  attendance_method: "MANUAL",

  overtime_minutes: 0,
  break_minutes: 0,

  notes: "",
}


// =========================================================
// HELPERS
// =========================================================

const isValidTime24 = (
  value
) => {
  if (!value) {
    return true
  }

  return TIME_24H_REGEX.test(
    value
  )
}


const normalizeTimeInput = (
  value
) => {
  return String(
    value || ""
  )
    .replace(
      /[^0-9:]/g,
      ""
    )
    .slice(
      0,
      5
    )
}


const formatTime24 = (
  value
) => {
  if (!value) {
    return "-"
  }

  const text =
    String(value)


  const timestampMatch =
    text.match(
      /(?:T|\s)(\d{2}):(\d{2})/
    )


  if (timestampMatch) {
    return (
      `${timestampMatch[1]}:${timestampMatch[2]}`
    )
  }


  const timeMatch =
    text.match(
      /^(\d{2}):(\d{2})/
    )


  if (timeMatch) {
    return (
      `${timeMatch[1]}:${timeMatch[2]}`
    )
  }


  return "-"
}


const getTimeValue = (
  value
) => {
  const formatted =
    formatTime24(
      value
    )

  return (
    formatted === "-"
      ? ""
      : formatted
  )
}


const formatDateFr = (
  value
) => {
  if (!value) {
    return "-"
  }

  const match =
    String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    )


  if (!match) {
    return value
  }


  return (
    `${match[3]}/${match[2]}/${match[1]}`
  )
}


const formatWorkedMinutes = (
  minutes
) => {
  const total =
    Number(
      minutes || 0
    )


  if (
    !Number.isFinite(total)
    || total <= 0
  ) {
    return "0h00"
  }


  const hours =
    Math.floor(
      total / 60
    )


  const remainingMinutes =
    total % 60


  return (
    `${hours}h${String(
      remainingMinutes
    ).padStart(
      2,
      "0"
    )}`
  )
}


const calculateEstimatedLateMinutes = (
  checkIn,
  scheduledStart
) => {
  if (
    !checkIn
    || !scheduledStart
    || !isValidTime24(
      checkIn
    )
  ) {
    return 0
  }


  const [
    checkHour,
    checkMinute,
  ] = checkIn
    .split(":")
    .map(Number)


  const [
    scheduleHour,
    scheduleMinute,
  ] = scheduledStart
    .split(":")
    .map(Number)


  const checkTotal =
    (
      checkHour * 60
    )
    + checkMinute


  const scheduleTotal =
    (
      scheduleHour * 60
    )
    + scheduleMinute


  return Math.max(
    0,
    checkTotal
    - scheduleTotal
  )
}


const sourceLabel = (
  source
) => {
  const labels = {
    MANUAL:
      "Manuel",

    BIOMETRIC:
      "Biométrique",

    RFID:
      "RFID",

    FACIAL:
      "Facial",

    DEVICE:
      "Appareil",

    IMPORT:
      "Import",

    API:
      "API",

    MOBILE:
      "Mobile",
  }


  return (
    labels[
      String(
        source || ""
      ).toUpperCase()
    ]
    || source
    || "-"
  )
}


// =========================================================
// KPI
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,

        minHeight:
          116,

        borderRadius:
          3,

        border:
          "1px solid",

        borderColor:
          "divider",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
      >
        <Box
          sx={{
            width:
              42,

            height:
              42,

            flexShrink:
              0,

            borderRadius:
              2,

            bgcolor:
              "action.hover",

            display:
              "grid",

            placeItems:
              "center",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function AttendancePage() {
  const [
    rows,
    setRows,
  ] = useState([])

  const [
    employees,
    setEmployees,
  ] = useState([])

  const [
    devices,
    setDevices,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    deleting,
    setDeleting,
  ] = useState(false)

  const [
    error,
    setError,
  ] = useState("")

  const [
    selectedDate,
    setSelectedDate,
  ] = useState(TODAY)

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all")

  const [
    manualDialogOpen,
    setManualDialogOpen,
  ] = useState(false)

  const [
    devicesDialogOpen,
    setDevicesDialogOpen,
  ] = useState(false)

  const [
    deleteDialogOpen,
    setDeleteDialogOpen,
  ] = useState(false)

  const [
    editingAttendance,
    setEditingAttendance,
  ] = useState(null)

  const [
    attendanceToDelete,
    setAttendanceToDelete,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState(
    EMPTY_FORM
  )

  const [
    scheduledStart,
    setScheduledStart,
  ] = useState(
    DEFAULT_WORK_START_TIME
  )

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  })


  // =======================================================
  // MESSAGE
  // =======================================================

  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }


  // =======================================================
  // FORM DATA
  // =======================================================

  const loadFormData =
    useCallback(
      async () => {
        try {

          const result =
            await hrAttendanceService
              .getAttendanceFormData()


          setEmployees(
            result?.employees
            || []
          )


          setDevices(
            result?.devices
            || []
          )


          setScheduledStart(
            result
              ?.attendance_rules
              ?.default_work_start_time
            || DEFAULT_WORK_START_TIME
          )

        } catch {

          setEmployees([])
          setDevices([])

          setScheduledStart(
            DEFAULT_WORK_START_TIME
          )
        }
      },
      []
    )


  // =======================================================
  // DONNÉES
  // =======================================================

  const loadData =
    useCallback(
      async () => {
        setLoading(true)
        setError("")

        try {

          const params = {
            attendance_date:
              selectedDate,

            status:
              statusFilter,
          }


          const [
            attendanceResult,
            summaryResult,
          ] = await Promise.all([
            hrAttendanceService
              .getAttendance(
                params
              ),

            hrAttendanceService
              .getAttendanceSummary(
                {
                  attendance_date:
                    selectedDate,
                }
              ),
          ])


          setRows(
            Array.isArray(
              attendanceResult
            )
              ? attendanceResult
              : attendanceResult
                  ?.items
                || []
          )


          setSummary(
            summaryResult
            || {}
          )

        } catch (err) {

          setError(
            err
              ?.response
              ?.data
              ?.detail
              ?.message
            || "Impossible de charger les présences."
          )

        } finally {

          setLoading(false)
        }
      },
      [
        selectedDate,
        statusFilter,
      ]
    )


  useEffect(() => {
    loadFormData()
  }, [
    loadFormData,
  ])


  useEffect(() => {
    loadData()
  }, [
    loadData,
  ])


  // =======================================================
  // RETARD ESTIMÉ
  // =======================================================

  const estimatedLateMinutes =
    useMemo(
      () =>
        calculateEstimatedLateMinutes(
          form.check_in,
          scheduledStart
        ),
      [
        form.check_in,
        scheduledStart,
      ]
    )


  const estimatedPresenceStatus =
    useMemo(
      () => {

        const administrativeStatus =
          String(
            form.status
            || "PRESENT"
          ).toUpperCase()


        if (
          administrativeStatus === "ABSENT"
          || administrativeStatus === "LEAVE"
          || administrativeStatus === "PERMISSION"
        ) {
          return administrativeStatus
        }


        return (
          estimatedLateMinutes > 0
            ? "LATE"
            : "PRESENT"
        )
      },
      [
        form.status,
        estimatedLateMinutes,
      ]
    )


  // =======================================================
  // OUVRIR CRÉATION
  // =======================================================

  const openCreateDialog = () => {
    setEditingAttendance(
      null
    )

    setForm({
      ...EMPTY_FORM,

      attendance_date:
        selectedDate,
    })

    setManualDialogOpen(
      true
    )
  }


  // =======================================================
  // OUVRIR MODIFICATION
  // =======================================================

  const openEditDialog = (
    row
  ) => {
    setEditingAttendance(
      row
    )

    setForm({
      employee_id:
        row.employee_id
        || "",

      attendance_date:
        row.attendance_date
        || selectedDate,

      check_in:
        getTimeValue(
          row.check_in
        ),

      check_out:
        getTimeValue(
          row.check_out
        ),

      status:
        row.status
        || "PRESENT",

      source:
        row.source
        || "MANUAL",

      attendance_method:
        row.attendance_method
        || "MANUAL",

      overtime_minutes:
        Number(
          row.overtime_minutes
          || 0
        ),

      break_minutes:
        Number(
          row.break_minutes
          || 0
        ),

      notes:
        row.notes
        || "",
    })

    setManualDialogOpen(
      true
    )
  }


  // =======================================================
  // VALIDATION FORMULAIRE
  // =======================================================

  const validateForm = () => {
    if (!form.employee_id) {
      showMessage(
        "Sélectionnez un employé.",
        "warning"
      )

      return false
    }


    if (
      form.check_in
      && !isValidTime24(
        form.check_in
      )
    ) {
      showMessage(
        "L'heure d'entrée doit être au format HH:mm, par exemple 07:45.",
        "warning"
      )

      return false
    }


    if (
      form.check_out
      && !isValidTime24(
        form.check_out
      )
    ) {
      showMessage(
        "L'heure de sortie doit être au format HH:mm, par exemple 17:00.",
        "warning"
      )

      return false
    }


    if (
      form.check_in
      && form.check_out
      && form.check_out
        < form.check_in
    ) {
      showMessage(
        "L'heure de sortie ne peut pas être antérieure à l'heure d'entrée.",
        "warning"
      )

      return false
    }


    return true
  }


  // =======================================================
  // CRÉER / MODIFIER
  // =======================================================

  const handleSaveAttendance =
    async () => {

      if (
        !validateForm()
      ) {
        return
      }


      setSaving(true)


      try {

        if (
          editingAttendance
        ) {

          if (
            typeof hrAttendanceService
              .updateAttendance
            !== "function"
          ) {
            showMessage(
              "Le service de modification du pointage n'est pas encore disponible.",
              "warning"
            )

            return
          }


          const payload = {
            check_in:
              form.check_in
              || null,

            check_out:
              form.check_out
              || null,

            status:
              form.status,

            source:
              form.source,

            attendance_method:
              form.attendance_method,

            overtime_minutes:
              Number(
                form.overtime_minutes
                || 0
              ),

            break_minutes:
              Number(
                form.break_minutes
                || 0
              ),

            notes:
              form.notes
              || null,
          }


          const result =
            await hrAttendanceService
              .updateAttendance(
                editingAttendance.id,
                payload
              )


          showMessage(
            result?.message
            || "Pointage modifié avec succès."
          )

        } else {

          const payload = {
            ...form,

            employee_id:
              Number(
                form.employee_id
              ),

            check_in:
              form.check_in
              || null,

            check_out:
              form.check_out
              || null,

            late_minutes:
              0,

            overtime_minutes:
              Number(
                form.overtime_minutes
                || 0
              ),

            break_minutes:
              Number(
                form.break_minutes
                || 0
              ),
          }


          const result =
            await hrAttendanceService
              .createAttendance(
                payload
              )


          showMessage(
            result?.message
            || "Présence enregistrée."
          )
        }


        setManualDialogOpen(
          false
        )

        setEditingAttendance(
          null
        )

        setForm({
          ...EMPTY_FORM,

          attendance_date:
            selectedDate,
        })


        await loadData()

      } catch (err) {

        showMessage(
          err
            ?.response
            ?.data
            ?.detail
            ?.message
          || (
            editingAttendance
              ? "Impossible de modifier le pointage."
              : "Impossible d'enregistrer le pointage."
          ),
          "error"
        )

      } finally {

        setSaving(false)
      }
    }


  // =======================================================
  // DEMANDE SUPPRESSION
  // =======================================================

  const openDeleteDialog = (
    row
  ) => {
    setAttendanceToDelete(
      row
    )

    setDeleteDialogOpen(
      true
    )
  }


  // =======================================================
  // SUPPRESSION
  // =======================================================

  const handleDeleteAttendance =
    async () => {

      if (
        !attendanceToDelete
      ) {
        return
      }


      if (
        typeof hrAttendanceService
          .deleteAttendance
        !== "function"
      ) {

        showMessage(
          "La route de suppression du pointage n'est pas encore disponible dans le service.",
          "warning"
        )

        setDeleteDialogOpen(
          false
        )

        return
      }


      setDeleting(true)


      try {

        const result =
          await hrAttendanceService
            .deleteAttendance(
              attendanceToDelete.id
            )


        showMessage(
          result?.message
          || "Pointage supprimé avec succès."
        )


        setDeleteDialogOpen(
          false
        )

        setAttendanceToDelete(
          null
        )


        await loadData()

      } catch (err) {

        showMessage(
          err
            ?.response
            ?.data
            ?.detail
            ?.message
          || "Impossible de supprimer le pointage.",
          "error"
        )

      } finally {

        setDeleting(false)
      }
    }


  // =======================================================
  // COLONNES
  // =======================================================

  const columns = useMemo(
    () => [
      {
        field:
          "employee_name",

        headerName:
          "Employé",

        minWidth:
          180,

        flex:
          1,
      },

      {
        field:
          "department_name",

        headerName:
          "Département",

        minWidth:
          150,

        renderCell:
          (params) =>
            params.value
            || "-",
      },

      {
        field:
          "position_name",

        headerName:
          "Poste",

        minWidth:
          150,

        renderCell:
          (params) =>
            params.value
            || "-",
      },

      {
        field:
          "attendance_date",

        headerName:
          "Date",

        minWidth:
          110,

        renderCell:
          (params) =>
            formatDateFr(
              params.value
            ),
      },

      {
        field:
          "check_in",

        headerName:
          "Entrée",

        minWidth:
          85,

        renderCell:
          (params) =>
            formatTime24(
              params.value
            ),
      },

      {
        field:
          "check_out",

        headerName:
          "Sortie",

        minWidth:
          85,

        renderCell:
          (params) =>
            formatTime24(
              params.value
            ),
      },

      {
        field:
          "worked_minutes",

        headerName:
          "Heures",

        minWidth:
          90,

        renderCell:
          (params) =>
            formatWorkedMinutes(
              params.value
            ),
      },

      {
        field:
          "overtime_minutes",

        headerName:
          "Suppl.",

        minWidth:
          95,

        renderCell:
          (params) => {

            const minutes =
              Number(
                params.value
                || 0
              )


            return (
              minutes > 0
                ? formatWorkedMinutes(
                    minutes
                  )
                : "-"
            )
          },
      },

      {
        field:
          "late_minutes",

        headerName:
          "Retard",

        minWidth:
          90,

        renderCell:
          (params) => {

            const minutes =
              Number(
                params.value
                || 0
              )


            return (
              minutes > 0
                ? `${minutes} min`
                : "-"
            )
          },
      },

      {
        field:
          "source",

        headerName:
          "Source",

        minWidth:
          100,

        renderCell:
          (params) =>
            sourceLabel(
              params.value
            ),
      },

      {
        field:
          "status",

        headerName:
          "Statut",

        minWidth:
          115,

        renderCell:
          (params) => {

            const status =
              String(
                params.value
                || ""
              ).toUpperCase()


            const config = {
              PRESENT: {
                label:
                  "Présent",

                color:
                  "success",
              },

              LATE: {
                label:
                  "Retard",

                color:
                  "warning",
              },

              ABSENT: {
                label:
                  "Absent",

                color:
                  "error",
              },

              LEAVE: {
                label:
                  "Congé",

                color:
                  "info",
              },

              PERMISSION: {
                label:
                  "Permission",

                color:
                  "secondary",
              },
            }


            const item =
              config[
                status
              ]
              || {
                label:
                  params.value
                  || "-",

                color:
                  "default",
              }


            return (
              <Chip
                size="small"
                label={
                  item.label
                }
                color={
                  item.color
                }
              />
            )
          },
      },

      {
        field:
          "actions",

        headerName:
          "Actions",

        minWidth:
          200,

        sortable:
          false,

        filterable:
          false,

        renderCell:
          (params) => (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                height:
                  "100%",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  openEditDialog(
                    params.row
                  )
                }
              >
                Modifier
              </Button>

              <Button
                size="small"
                color="error"
                onClick={() =>
                  openDeleteDialog(
                    params.row
                  )
                }
              >
                Supprimer
              </Button>
            </Stack>
          ),
      },
    ],
    []
  )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>

      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },
        }}
      >

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          mb={2}
        >

          <Box>

            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs:
                    "1.7rem",

                  md:
                    "2.125rem",
                },
              }}
            >
              Présences & pointage
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
            >
              Entrées, sorties, retards,
              absences, heures travaillées,
              heures supplémentaires et
              intégration des dispositifs
              de pointage.
            </Typography>

          </Box>


          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >

            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={
                loadData
              }
              disabled={
                loading
              }
            >
              Actualiser
            </Button>


            <Button
              variant="contained"
              startIcon={
                <AddIcon />
              }
              onClick={
                openCreateDialog
              }
            >
              Pointage manuel
            </Button>


            <Button
              variant="outlined"
              startIcon={
                <FileUploadOutlinedIcon />
              }
            >
              Importer
            </Button>


            <Button
              variant="outlined"
              startIcon={
                <DevicesOutlinedIcon />
              }
              onClick={() =>
                setDevicesDialogOpen(
                  true
                )
              }
            >
              Appareils
            </Button>

          </Stack>

        </Stack>


        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          RLSys peut recevoir les
          pointages manuels, biométriques,
          RFID, faciaux, mobiles, fichiers
          importés ou événements API.
          {" "}
          L'heure normale actuelle est
          {" "}
          <strong>
            {scheduledStart}
          </strong>
          .
          Le retard est calculé
          automatiquement par le système.
        </Alert>


        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
            }}
          >
            {error}
          </Alert>
        )}


        {/* ================================================= */}
        {/* FILTRES */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,

            borderRadius:
              3,

            border:
              "1px solid",

            borderColor:
              "divider",
          }}
        >

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
          >

            <TextField
              label="Date"
              type="date"
              value={
                selectedDate
              }
              onChange={
                (event) =>
                  setSelectedDate(
                    event.target.value
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />


            <TextField
              select
              label="Statut"
              value={
                statusFilter
              }
              onChange={
                (event) =>
                  setStatusFilter(
                    event.target.value
                  )
              }
              fullWidth
            >

              <MenuItem
                value="all"
              >
                Tous les statuts
              </MenuItem>

              <MenuItem
                value="PRESENT"
              >
                Présent
              </MenuItem>

              <MenuItem
                value="LATE"
              >
                Retard
              </MenuItem>

              <MenuItem
                value="ABSENT"
              >
                Absent
              </MenuItem>

              <MenuItem
                value="LEAVE"
              >
                Congé
              </MenuItem>

              <MenuItem
                value="PERMISSION"
              >
                Permission
              </MenuItem>

            </TextField>

          </Stack>

        </Paper>


        {/* ================================================= */}
        {/* KPIS */}
        {/* ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns: {
              xs:
                "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              lg:
                "repeat(3, minmax(0, 1fr))",

              xl:
                "repeat(6, minmax(0, 1fr))",
            },

            gap:
              2,

            mb:
              2,
          }}
        >

          <KpiCard
            title="Présents"
            value={
              summary
                .present_count
              || 0
            }
            subtitle="Aujourd'hui"
            icon={
              <CheckCircleIcon />
            }
          />


          <KpiCard
            title="Retards"
            value={
              summary
                .late_count
              || 0
            }
            subtitle={
              `Après ${scheduledStart}`
            }
            icon={
              <ScheduleOutlinedIcon />
            }
          />


          <KpiCard
            title="Absents"
            value={
              summary
                .absent_count
              || 0
            }
            subtitle="Absences"
            icon={
              <EventBusyOutlinedIcon />
            }
          />


          <KpiCard
            title="Congés"
            value={
              summary
                .leave_count
              || 0
            }
            subtitle="Personnel en congé"
            icon={
              <AccessTimeOutlinedIcon />
            }
          />


          <KpiCard
            title="Heures travaillées"
            value={
              formatWorkedMinutes(
                summary
                  .worked_minutes
                || 0
              )
            }
            subtitle="Total du jour"
            icon={
              <AccessTimeOutlinedIcon />
            }
          />


          <KpiCard
            title="Heures supplémentaires"
            value={
              formatWorkedMinutes(
                summary
                  .overtime_minutes
                || 0
              )
            }
            subtitle="Total supplémentaire"
            icon={
              <ScheduleOutlinedIcon />
            }
          />

        </Box>


        {/* ================================================= */}
        {/* TABLEAU */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            borderRadius:
              3,

            border:
              "1px solid",

            borderColor:
              "divider",

            overflow:
              "hidden",
          }}
        >

          <Box
            sx={{
              p: 2,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >

            <Typography
              variant="h6"
              fontWeight={800}
            >
              Registre des présences
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
            >
              Historique journalier des
              pointages, heures normales,
              heures supplémentaires et
              statuts.
            </Typography>

          </Box>


          <Box
            sx={{
              width:
                "100%",

              height: {
                xs:
                  470,

                md:
                  560,
              },
            }}
          >

            <DataGrid
              rows={
                rows
              }
              columns={
                columns
              }
              loading={
                loading
              }
              disableRowSelectionOnClick

              pageSizeOptions={[
                5,
                10,
                25,
              ]}

              initialState={{
                pagination: {
                  paginationModel: {
                    page:
                      0,

                    pageSize:
                      10,
                  },
                },
              }}

              sx={{
                border:
                  0,

                minWidth:
                  1350,
              }}
            />

          </Box>

        </Paper>


        {/* ================================================= */}
        {/* CRÉATION / MODIFICATION */}
        {/* ================================================= */}

        <Dialog
          open={
            manualDialogOpen
          }
          onClose={() =>
            !saving
            && setManualDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="md"
        >

          <DialogTitle
            fontWeight={900}
          >
            {
              editingAttendance
                ? "Modifier le pointage"
                : "Nouveau pointage manuel"
            }
          </DialogTitle>


          <DialogContent>

            <Alert
              severity="info"
              sx={{
                mt: 1,
                mb: 2,
              }}
            >
              Pour une présence,
              le statut
              {" "}
              <strong>
                Présent / Retard
              </strong>
              {" "}
              et les minutes de retard
              sont calculés automatiquement
              à partir de
              {" "}
              <strong>
                {scheduledStart}
              </strong>
              .
            </Alert>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns: {
                  xs:
                    "1fr",

                  md:
                    "repeat(2, 1fr)",
                },

                gap:
                  2,
              }}
            >

              <TextField
                select
                required
                disabled={
                  Boolean(
                    editingAttendance
                  )
                }
                label="Employé"
                value={
                  form.employee_id
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        employee_id:
                          event.target.value,
                      })
                    )
                }
              >

                {employees.map(
                  (employee) => (
                    <MenuItem
                      key={
                        employee.id
                      }
                      value={
                        employee.id
                      }
                    >
                      {employee.employee_code}
                      {" - "}
                      {employee.first_name}
                      {" "}
                      {employee.last_name}
                    </MenuItem>
                  )
                )}

              </TextField>


              <TextField
                required
                disabled={
                  Boolean(
                    editingAttendance
                  )
                }
                label="Date"
                type="date"
                value={
                  form.attendance_date
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        attendance_date:
                          event.target.value,
                      })
                    )
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />


              <TextField
                label="Heure d'entrée"
                placeholder="HH:mm"
                value={
                  form.check_in
                }
                error={
                  Boolean(
                    form.check_in
                  )
                  && !isValidTime24(
                    form.check_in
                  )
                }
                helperText={
                  form.check_in
                  && !isValidTime24(
                    form.check_in
                  )
                    ? "Format attendu : 00:00 à 23:59"
                    : `Horaire normal : ${scheduledStart}`
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        check_in:
                          normalizeTimeInput(
                            event.target.value
                          ),
                      })
                    )
                }
                inputProps={{
                  maxLength:
                    5,

                  inputMode:
                    "numeric",
                }}
              />


              <TextField
                label="Heure de sortie"
                placeholder="HH:mm"
                value={
                  form.check_out
                }
                error={
                  Boolean(
                    form.check_out
                  )
                  && !isValidTime24(
                    form.check_out
                  )
                }
                helperText={
                  form.check_out
                  && !isValidTime24(
                    form.check_out
                  )
                    ? "Format attendu : 00:00 à 23:59"
                    : "Exemple : 17:30"
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        check_out:
                          normalizeTimeInput(
                            event.target.value
                          ),
                      })
                    )
                }
                inputProps={{
                  maxLength:
                    5,

                  inputMode:
                    "numeric",
                }}
              />


              <TextField
                select
                label="Situation"
                value={
                  form.status
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        status:
                          event.target.value,
                      })
                    )
                }
              >

                <MenuItem
                  value="PRESENT"
                >
                  Présence normale
                </MenuItem>

                <MenuItem
                  value="ABSENT"
                >
                  Absent
                </MenuItem>

                <MenuItem
                  value="LEAVE"
                >
                  Congé
                </MenuItem>

                <MenuItem
                  value="PERMISSION"
                >
                  Permission
                </MenuItem>

              </TextField>


              <TextField
                label="Retard calculé"
                value={
                  estimatedLateMinutes > 0
                    ? `${estimatedLateMinutes} minute(s)`
                    : "Aucun retard"
                }
                InputProps={{
                  readOnly:
                    true,
                }}
                helperText={
                  estimatedPresenceStatus === "LATE"
                    ? "Le statut final sera automatiquement : Retard"
                    : form.status === "PRESENT"
                      ? "Le statut final sera automatiquement : Présent"
                      : "Le statut administratif sélectionné sera conservé."
                }
              />


              <TextField
                label="Minutes supplémentaires"
                type="number"
                value={
                  form.overtime_minutes
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        overtime_minutes:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
                helperText={
                  Number(
                    form.overtime_minutes
                    || 0
                  ) > 0
                    ? `Soit ${formatWorkedMinutes(
                        Number(
                          form.overtime_minutes
                          || 0
                        )
                      )}`
                    : "Aucune heure supplémentaire"
                }
              />


              <TextField
                label="Pause en minutes"
                type="number"
                value={
                  form.break_minutes
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        break_minutes:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
              />


              <TextField
                label="Notes"
                multiline
                minRows={3}
                value={
                  form.notes
                }
                onChange={
                  (event) =>
                    setForm(
                      (prev) => ({
                        ...prev,

                        notes:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    xs:
                      "1",

                    md:
                      "1 / -1",
                  },
                }}
              />

            </Box>

          </DialogContent>


          <DialogActions>

            <Button
              onClick={() => {
                setManualDialogOpen(
                  false
                )

                setEditingAttendance(
                  null
                )
              }}
              disabled={
                saving
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"
              onClick={
                handleSaveAttendance
              }
              disabled={
                saving
              }
            >
              {
                editingAttendance
                  ? "Enregistrer les modifications"
                  : "Enregistrer le pointage"
              }
            </Button>

          </DialogActions>

        </Dialog>


        {/* ================================================= */}
        {/* CONFIRMATION SUPPRESSION */}
        {/* ================================================= */}

        <Dialog
          open={
            deleteDialogOpen
          }
          onClose={() =>
            !deleting
            && setDeleteDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="xs"
        >

          <DialogTitle
            fontWeight={900}
          >
            Supprimer le pointage
          </DialogTitle>


          <DialogContent>

            <Alert
              severity="warning"
              sx={{
                mt: 1,
              }}
            >
              Cette action supprimera le
              pointage sélectionné.
            </Alert>


            {attendanceToDelete && (

              <Box
                sx={{
                  mt:
                    2,
                }}
              >

                <Typography
                  fontWeight={800}
                >
                  {
                    attendanceToDelete
                      .employee_name
                    || "Employé"
                  }
                </Typography>


                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Date :
                  {" "}
                  {
                    formatDateFr(
                      attendanceToDelete
                        .attendance_date
                    )
                  }
                  {" • "}
                  Entrée :
                  {" "}
                  {
                    formatTime24(
                      attendanceToDelete
                        .check_in
                    )
                  }
                  {" • "}
                  Sortie :
                  {" "}
                  {
                    formatTime24(
                      attendanceToDelete
                        .check_out
                    )
                  }
                </Typography>

              </Box>

            )}

          </DialogContent>


          <DialogActions>

            <Button
              onClick={() =>
                setDeleteDialogOpen(
                  false
                )
              }
              disabled={
                deleting
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"
              color="error"
              onClick={
                handleDeleteAttendance
              }
              disabled={
                deleting
              }
            >
              Supprimer
            </Button>

          </DialogActions>

        </Dialog>


        {/* ================================================= */}
        {/* APPAREILS */}
        {/* ================================================= */}

        <Dialog
          open={
            devicesDialogOpen
          }
          onClose={() =>
            setDevicesDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="md"
        >

          <DialogTitle
            fontWeight={900}
          >
            Appareils de pointage
          </DialogTitle>


          <DialogContent>

            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
              }}
            >
              Les appareils enregistrés
              pourront être reliés aux
              événements de pointage
              biométriques, RFID, faciaux
              ou API.
            </Typography>


            <Stack
              spacing={1}
            >

              {devices.length === 0
                ? (

                  <Alert
                    severity="info"
                  >
                    Aucun appareil de
                    pointage enregistré.
                  </Alert>

                )
                : (

                  devices.map(
                    (device) => (

                      <Paper
                        key={
                          device.id
                        }
                        elevation={0}
                        sx={{
                          p:
                            2,

                          border:
                            "1px solid",

                          borderColor:
                            "divider",

                          borderRadius:
                            2,
                        }}
                      >

                        <Stack
                          direction={{
                            xs:
                              "column",

                            sm:
                              "row",
                          }}
                          justifyContent="space-between"
                          spacing={1}
                        >

                          <Box>

                            <Typography
                              fontWeight={800}
                            >
                              {device.code}
                              {" - "}
                              {device.name}
                            </Typography>


                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {device.device_type}
                              {" • "}
                              {
                                device.location_name
                                || "Emplacement non défini"
                              }
                            </Typography>

                          </Box>


                          <Chip
                            size="small"
                            label={
                              device.status
                            }
                            color={
                              device.status === "ACTIVE"
                                ? "success"
                                : "default"
                            }
                          />

                        </Stack>

                      </Paper>

                    )
                  )

                )}

            </Stack>

          </DialogContent>


          <DialogActions>

            <Button
              onClick={() =>
                setDevicesDialogOpen(
                  false
                )
              }
            >
              Fermer
            </Button>

          </DialogActions>

        </Dialog>


        {/* ================================================= */}
        {/* SNACKBAR */}
        {/* ================================================= */}

        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            3500
          }
          onClose={() =>
            setSnackbar(
              (prev) => ({
                ...prev,

                open:
                  false,
              })
            )
          }
          anchorOrigin={{
            vertical:
              "top",

            horizontal:
              "right",
          }}
        >

          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
          >
            {snackbar.message}
          </Alert>

        </Snackbar>

      </Box>

    </MainLayout>
  )
}